<template>
  <FormDialog
    ref="formDialog"
    :after-close="handleClose"
    :after-save="handleSave"
    :persistent="false"
    :schema-id="schema?.id"
    :sync-route-query="false"
    data-testid="complaint-form-dialog"
    title="Report a complaint"
    draft
  />
</template>

<script setup>
import { useStore } from 'vuex';

import FormDialog from '@/shared/components/form/FormDialog.vue';
import useApi from '@/shared/composables/useApi';
import useEventBus from '@/shared/composables/useEventBus';

const { api: Api } = useApi();
const eventBus = useEventBus();
const formDialog = ref(null);
const schema = ref(null);
const store = useStore();

function handleClose() {
  schema.value = null;
}

function handleSave() {
  eventBus.chime('Submitted');
}

function complaintFormSchemaId() {
  return store.state.config?.complaint_form_schema_id;
}

function complaintFormSchemaFromStore() {
  const schemaId = complaintFormSchemaId();
  if (!schemaId) return null;

  return store.state.schemas?.[schemaId] ?? null;
}

async function ensureSession() {
  if (store.state.is_authenticated) return true;

  const response = await Api.member.create({
    default_locale: store.state.locale,
    is_anonymous: true,
    organization_id: window.organization_id,
    is_parent: true,
  });

  if (!response?.data) return false;

  const profile = {
    ...response.data,
    organization_id: response.data.organization_id ?? window.organization_id,
    is_anonymous: response.data.is_anonymous ?? true,
    is_parent: response.data.is_parent ?? true,
  };

  await store.dispatch('identify', { profile });

  return store.state.is_authenticated;
}

async function loadComplaintFormSchema(schemaId) {
  const response = await Api.publicApi.organization.schema.get(schemaId);
  if (!response?.data) return null;

  store.commit('setSchemas', {
    ...store.state.schemas,
    [response.data.id]: response.data,
  });

  return response.data;
}

async function open() {
  if (formDialog.value?.isOpen?.()) return;

  const schemaId = complaintFormSchemaId();
  if (!schemaId) return;

  const loadedSchema = complaintFormSchemaFromStore() ?? (await loadComplaintFormSchema(schemaId));
  if (!loadedSchema) {
    eventBus.error('Form could not be loaded');
    return;
  }

  if (!(await ensureSession())) return;

  schema.value = loadedSchema;
  await nextTick();
  await formDialog.value?.open();

  if (!formDialog.value?.isOpen?.()) {
    schema.value = null;
  }
}

defineExpose({ open });
</script>
